import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, MapPin, Loader2, Package, Clock, User, Zap, Moon, Briefcase, Sun, Navigation, Store } from "lucide-react";
import { api } from "../lib/api";
import { rememberCustomerEmail, flushPendingCustomerToken, initCustomerNativePush, isNative } from "../lib/customer-native";
import AddressAutocomplete from "../components/AddressAutocomplete";
import FeasibilityPanel from "../components/FeasibilityPanel";
import PickupPointSelector from "../components/PickupPointSelector";

const PICKUP_TIMES = ["18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00"];
const PRICE = 199;
const EXTRA_PRICE = 50;

// Returns true if a HH:MM slot has already passed (with 15-min lead time)
// considering whether the user has selected today's date.
function slotIsPast(dateStr, timeStr) {
  if (!dateStr || !timeStr) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (dateStr !== today) return false;
  const [h, m] = timeStr.split(":").map(Number);
  const slot = new Date();
  slot.setHours(h, m, 0, 0);
  return slot.getTime() < Date.now() + 15 * 60 * 1000;
}

function nextAvailableDate(timeStr) {
  // If today's chosen pickup time has passed, return tomorrow's date
  if (slotIsPast(new Date().toISOString().slice(0, 10), timeStr)) {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }
  return new Date().toISOString().slice(0, 10);
}

const DELIVERY_TYPES = [
  { id: "same_day", label: "Same-Day Kurér", price: 199, basePrice: 199, fromLabel: "Fra 199 kr inkl. 8 km", icon: Sun, desc: "Direkte A→B · same-day", color: "border-brand-dark bg-brand-dark text-white" },
  { id: "express", label: "Express Kurér", price: 289, basePrice: 289, fromLabel: "Fra 289 kr inkl. 8 km", icon: Zap, desc: "Levering inden 90 min", color: "border-amber-500 bg-amber-500 text-white" },
  { id: "evening", label: "Aftenlevering", price: 249, basePrice: 249, fromLabel: "Fra 249 kr inkl. 8 km", icon: Moon, desc: "Afhentning & levering 18–22", color: "border-indigo-700 bg-indigo-700 text-white" },
  { id: "business", label: "Erhvervskurér", price: null, basePrice: null, fromLabel: "Kontakt os for tilbud", icon: Briefcase, desc: "Fast aftale — kontakt os for tilbud", color: "border-slate-700 bg-slate-700 text-white", quote_only: true },
];

const STEPS = [
  { k: 1, label: "Kontakt", i: User },
  { k: 2, label: "Adresser", i: MapPin },
  { k: 3, label: "Pakke", i: Package },
  { k: 4, label: "Bekræft", i: CheckCircle2 },
];

const calcSubtotal = (n) => PRICE + Math.max(0, Number(n || 1) - 1) * EXTRA_PRICE;

export default function BookingPage() {
  const [params] = useSearchParams();
  const [step, setStep] = React.useState(1);
  const [discount, setDiscount] = React.useState({ code: "", status: "idle", info: null });
  const [availability, setAvailability] = React.useState(null); // {accepting_orders, message}

  // Check whether bookings are open (auto-disabled when no drivers on duty)
  React.useEffect(() => {
    api.get("/availability")
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability({ accepting_orders: true })); // fail-open on network error
  }, []);

  // Auto-fill referral code from URL (?ref=REFXXXXXX) or partner promo (?promo=GENFIND5)
  React.useEffect(() => {
    const ref = params.get("ref");
    const promo = params.get("promo");
    if (ref && /^REF[A-F0-9]{6}$/i.test(ref)) {
      setDiscount({ code: ref.toUpperCase(), status: "idle", info: null });
    } else if (promo) {
      // Partner promo codes (GENFIND5, etc.) — auto-fill so user can see it pre-applied
      setDiscount({ code: promo.toUpperCase(), status: "idle", info: null });
    }
  }, [params]);
  const [form, setForm] = React.useState({
    customer_type: "private",
    customer_first_name: "",
    customer_last_name: "",
    customer_email: "",
    customer_phone: "",
    company_name: "",
    cvr: "",
    pickup_address: "",
    pickup_city: "",
    pickup_zip: "",
    delivery_address: "",
    delivery_city: "",
    delivery_zip: "",
    recipient_first_name: "",
    recipient_last_name: "",
    recipient_phone: "",
    delivery_type: "same_day",
    pickup_date: new Date().toISOString().slice(0, 10),
    package_count: 1,
    weight_kg: 1,
    pickup_time: "19:00",
    notes: "",
    delivery_instruction: "",
    insurance: false,
    // EMA Pickup Point — when set, delivery is to a pickup point partner
    pakkeshop_partner_id: "",
    pakkeshop_name: "",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [quote, setQuote] = React.useState(null);
  const [quoteLoading, setQuoteLoading] = React.useState(false);
  const [quoteError, setQuoteError] = React.useState(null);
  // EMA Pickup Point toggle — when ON, the customer chooses a partner store as delivery destination
  const [useDeliveryToPickup, setUseDeliveryToPickup] = React.useState(false);

  const subtotal = calcSubtotal(form.package_count);
  const isBusinessQuote = form.delivery_type === "business";
  // Use server-quoted total if available, else fallback to client estimate
  const liveTotal = quote && !quote.quote_only ? quote.total_dkk : subtotal;
  const total = discount.status === "valid"
    ? Math.max(0, liveTotal - (discount.info?.discount_dkk || 0))
    : liveTotal;
  const cancelled = params.get("cancelled");
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Genbestil — prefill form from a previous booking when ?reorder=BN&e=email
  const reorderBn = params.get("reorder");
  const reorderEmail = params.get("e");
  const [reorderState, setReorderState] = React.useState(reorderBn ? "loading" : "idle");
  React.useEffect(() => {
    if (!reorderBn || !reorderEmail) return;
    let cancelledLocal = false;
    (async () => {
      try {
        const { data } = await api.post("/track/reorder", {
          booking_number: reorderBn,
          email: reorderEmail,
        });
        if (cancelledLocal || !data?.prefill) return;
        const p = data.prefill;
        // Split full names back into first/last for the form
        const splitName = (full) => {
          if (!full) return ["", ""];
          const parts = full.trim().split(/\s+/);
          if (parts.length === 1) return [parts[0], ""];
          return [parts.slice(0, -1).join(" "), parts[parts.length - 1]];
        };
        const [cFirst, cLast] = splitName(p.customer_name);
        const [rFirst, rLast] = splitName(p.recipient_name);
        setForm((f) => ({
          ...f,
          customer_first_name: cFirst || f.customer_first_name,
          customer_last_name: cLast || f.customer_last_name,
          customer_email: p.customer_email || f.customer_email,
          customer_phone: p.customer_phone || f.customer_phone,
          pickup_address: p.pickup_address || f.pickup_address,
          pickup_zip: p.pickup_zip || f.pickup_zip,
          pickup_city: p.pickup_city || f.pickup_city,
          delivery_address: p.delivery_address || f.delivery_address,
          delivery_zip: p.delivery_zip || f.delivery_zip,
          delivery_city: p.delivery_city || f.delivery_city,
          recipient_first_name: rFirst || f.recipient_first_name,
          recipient_last_name: rLast || f.recipient_last_name,
          recipient_phone: p.recipient_phone || f.recipient_phone,
          delivery_type: p.delivery_type || f.delivery_type,
          package_count: p.package_count || f.package_count,
          weight_kg: p.weight_kg || f.weight_kg,
          notes: p.notes || f.notes,
          delivery_instruction: p.delivery_instruction || f.delivery_instruction,
        }));
        setReorderState("loaded");
      } catch (e) {
        if (!cancelledLocal) setReorderState("error");
      }
    })();
    return () => { cancelledLocal = true; };
  }, [reorderBn, reorderEmail]);

  // Prefill pickup/delivery from URL params (e.g. fra hero-prisberegneren på forsiden)
  React.useEffect(() => {
    const fields = {
      pickup_address: params.get("pa"),
      pickup_zip: params.get("pz"),
      pickup_city: params.get("pc"),
      delivery_address: params.get("da"),
      delivery_zip: params.get("dz"),
      delivery_city: params.get("dc"),
      delivery_type: params.get("type") || params.get("dt"),
    };
    const hasAny = Object.values(fields).some((v) => v);
    if (!hasAny) return;
    setForm((f) => {
      const next = { ...f };
      Object.entries(fields).forEach(([k, v]) => { if (v) next[k] = v; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select pickup point from URL: /book?delivery_to_pickup={shop_id}
  React.useEffect(() => {
    const ppId = params.get("delivery_to_pickup");
    if (!ppId) return;
    setUseDeliveryToPickup(true);
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    fetch(`${API}/pickup-points/${ppId}`)
      .then((r) => r.json())
      .then((data) => {
        const p = data?.pickup_point;
        if (!p) return;
        setForm((f) => ({
          ...f,
          pakkeshop_partner_id: p.id,
          pakkeshop_name: p.shop_name,
          delivery_address: p.shop_address || "",
          delivery_zip: p.shop_zip || "",
          delivery_city: p.shop_city || "",
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-shift to tomorrow if all today's same-day slots have passed
  React.useEffect(() => {
    if (form.delivery_type !== "same_day") return;
    const today = new Date().toISOString().slice(0, 10);
    if (form.pickup_date !== today) return;
    const lastSlotPast = slotIsPast(today, PICKUP_TIMES[PICKUP_TIMES.length - 1]);
    if (lastSlotPast) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setForm((f) => ({ ...f, pickup_date: tomorrow.toISOString().slice(0, 10), pickup_time: PICKUP_TIMES[0] }));
      return;
    }
    // If current slot is past but later ones are available, jump to next available
    if (slotIsPast(today, form.pickup_time)) {
      const next = PICKUP_TIMES.find((t) => !slotIsPast(today, t));
      if (next) setForm((f) => ({ ...f, pickup_time: next }));
    }
  }, [form.delivery_type, form.pickup_date, form.pickup_time]);
  // Auto-fetch live quote when address + delivery type known
  React.useEffect(() => {
    const hasAddresses =
      form.pickup_address && form.pickup_zip && form.pickup_city &&
      form.delivery_address && form.delivery_zip && form.delivery_city;
    if (!hasAddresses || isBusinessQuote) { setQuote(null); setQuoteError(null); return; }
    setQuoteLoading(true);
    setQuoteError(null);
    const timer = setTimeout(async () => {
      const tryFetch = async (attempt = 1) => {
        try {
          const { data } = await api.post("/quote", {
            pickup_address: form.pickup_address,
            pickup_zip: form.pickup_zip,
            pickup_city: form.pickup_city,
            delivery_address: form.delivery_address,
            delivery_zip: form.delivery_zip,
            delivery_city: form.delivery_city,
            delivery_type: form.delivery_type,
            pickup_date: form.pickup_date,
            pickup_time: form.pickup_time,
            package_count: Number(form.package_count || 1),
            insurance: !!form.insurance,
          });
          setQuote(data);
          setQuoteError(null);
        } catch (e) {
          const detail = e?.response?.data?.detail || "";
          const isGeocodeErr = /finde|adresse/i.test(String(detail));
          // Retry once on transient errors
          if (attempt === 1 && !isGeocodeErr) {
            setTimeout(() => tryFetch(2), 1500);
            return;
          }
          setQuote(null);
          setQuoteError(detail || "Kunne ikke beregne pris");
        } finally {
          setQuoteLoading(false);
        }
      };
      tryFetch(1);
    }, 700);
    return () => clearTimeout(timer);
  }, [form.pickup_address, form.pickup_zip, form.pickup_city,
      form.delivery_address, form.delivery_zip, form.delivery_city,
      form.delivery_type, form.pickup_date, form.pickup_time,
      form.package_count, form.insurance, isBusinessQuote]);

  // Feasibility check — same trigger as quote, parallel call
  const [feas, setFeas] = React.useState(null);
  const [feasLoading, setFeasLoading] = React.useState(false);
  React.useEffect(() => {
    const hasAddresses =
      form.pickup_address && form.pickup_zip && form.pickup_city &&
      form.delivery_address && form.delivery_zip && form.delivery_city;
    if (!hasAddresses || isBusinessQuote) { setFeas(null); return; }
    setFeasLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/booking/feasibility", {
          pickup_address: form.pickup_address,
          pickup_zip: form.pickup_zip,
          pickup_city: form.pickup_city,
          delivery_address: form.delivery_address,
          delivery_zip: form.delivery_zip,
          delivery_city: form.delivery_city,
          delivery_type: form.delivery_type,
          pickup_date: form.pickup_date || null,
          pickup_time: form.pickup_time || null,
        });
        setFeas(data);
      } catch (e) {
        // Failsafe: if feasibility check itself fails (network), do not block booking
        setFeas(null);
      } finally { setFeasLoading(false); }
    }, 800);
    return () => clearTimeout(timer);
  }, [form.pickup_address, form.pickup_zip, form.pickup_city,
      form.delivery_address, form.delivery_zip, form.delivery_city,
      form.delivery_type, form.pickup_date, form.pickup_time,
      isBusinessQuote]);

  const feasBlocking = feas && feas.feasible === false;

  const applyDiscount = async () => {
    if (!discount.code) { toast.error("Indtast en kode"); return; }
    setDiscount((d) => ({ ...d, status: "checking" }));
    try {
      const { data } = await api.post("/discount/validate", {
        code: discount.code,
        package_count: Number(form.package_count || 1),
      });
      setDiscount({ code: discount.code, status: "valid", info: data });
      toast.success(`Rabat anvendt: -${data.discount_dkk} kr`);
    } catch (e) {
      setDiscount({ code: discount.code, status: "invalid", info: null });
      toast.error(e?.response?.data?.detail || "Ugyldig kode");
    }
  };
  const removeDiscount = () => setDiscount({ code: "", status: "idle", info: null });

  const validateStep = () => {
    if (step === 1) {
      if (!form.customer_first_name.trim()) return "Fornavn skal udfyldes";
      if (form.customer_first_name.trim().length < 2) return "Fornavn skal være mindst 2 tegn";
      if (!form.customer_last_name.trim()) return "Efternavn skal udfyldes";
      if (form.customer_last_name.trim().length < 2) return "Efternavn skal være mindst 2 tegn";
      if (!form.customer_email.trim()) return "Email skal udfyldes";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.customer_email.trim())) return "Ugyldig email — fx navn@example.dk";
      if (!form.customer_phone.trim()) return "Mobilnummer skal udfyldes";
      const digits = (form.customer_phone.match(/\d/g) || []).length;
      if (digits < 8) return "Ugyldigt mobilnummer — mindst 8 cifre (fx +45 12 34 56 78)";
      if (digits > 15) return "Mobilnummer er for langt";
      if (form.customer_type === "business" && !form.company_name.trim()) return "Firmanavn skal udfyldes";
      if (form.customer_type === "business" && form.cvr && !/^\d{8}$/.test(form.cvr.replace(/\s/g, ""))) return "CVR skal være 8 cifre";
    }
    if (step === 2) {
      if (!form.pickup_address.trim()) return "Afhentningsadresse skal udfyldes";
      if (!/\d/.test(form.pickup_address)) return "Afhentningsadresse skal indeholde husnummer (fx Vesterbrogade 10)";
      if (!form.pickup_zip.trim()) return "Afhentnings-postnr skal udfyldes";
      if (!/^\d{4}$/.test(form.pickup_zip.trim())) return "Afhentnings-postnr skal være 4 cifre";
      if (!form.pickup_city.trim()) return "Afhentnings-by skal udfyldes";
      if (useDeliveryToPickup) {
        if (!form.pakkeshop_partner_id) return "Vælg en EMA Pickup Point som leveringsadresse";
      } else {
        if (!form.delivery_address.trim()) return "Leveringsadresse skal udfyldes";
        if (!/\d/.test(form.delivery_address)) return "Leveringsadresse skal indeholde husnummer";
        if (!form.delivery_zip.trim()) return "Leverings-postnr skal udfyldes";
        if (!/^\d{4}$/.test(form.delivery_zip.trim())) return "Leverings-postnr skal være 4 cifre";
        if (!form.delivery_city.trim()) return "Leverings-by skal udfyldes";
      }
      // Recipient
      if (!form.recipient_first_name.trim()) return "Modtagerens fornavn skal udfyldes";
      if (form.recipient_first_name.trim().length < 2) return "Modtagerens fornavn skal være mindst 2 tegn";
      if (!form.recipient_last_name.trim()) return "Modtagerens efternavn skal udfyldes";
      if (form.recipient_last_name.trim().length < 2) return "Modtagerens efternavn skal være mindst 2 tegn";
      if (!form.recipient_phone.trim()) return "Modtagerens mobilnummer skal udfyldes";
      {
        const d = (form.recipient_phone.match(/\d/g) || []).length;
        if (d < 8) return "Modtagerens mobilnummer skal indeholde mindst 8 cifre";
        if (d > 15) return "Modtagerens mobilnummer er for langt";
      }
    }
    if (step === 3) {
      if (form.weight_kg > 11) return "Maks 11 kg pr. pakke";
      if (form.weight_kg <= 0) return "Vægt skal være større end 0";
      if (form.package_count < 1) return "Mindst 1 pakke";
      if (form.package_count > 20) return "Maks 20 pakker pr. booking";
      if (slotIsPast(form.pickup_date, form.pickup_time)) {
        return "Afhentningstidspunktet er allerede passeret. Vælg en senere tid eller en anden dato.";
      }
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    if (isBusinessQuote) {
      window.location.href = "/kontakt?subject=Erhverv";
      return;
    }
    setSubmitting(true);
    try {
      const fullName = `${form.customer_first_name.trim()} ${form.customer_last_name.trim()}`.trim();
      const recipientFullName = `${form.recipient_first_name.trim()} ${form.recipient_last_name.trim()}`.trim();
      const sourceParam = params.get("source");
      const payload = {
        ...form,
        customer_name: fullName,
        recipient_name: recipientFullName,
        package_count: Number(form.package_count),
        weight_kg: Number(form.weight_kg),
        discount_code: discount.status === "valid" ? discount.code : undefined,
        source: sourceParam || undefined,
      };
      delete payload.customer_first_name;
      delete payload.customer_last_name;
      delete payload.recipient_first_name;
      delete payload.recipient_last_name;
      if (form.customer_type !== "business") {
        payload.company_name = null;
        payload.cvr = null;
      }
      const { data: booking } = await api.post("/bookings", payload);
      // Remember email for customer-native-app push notifications
      try {
        if (booking?.customer_email) {
          rememberCustomerEmail(booking.customer_email);
          if (isNative()) {
            await flushPendingCustomerToken(booking.customer_email);
            initCustomerNativePush(booking.customer_email).catch(() => {});
          }
        }
      } catch (pushErr) { console.warn("Push setup non-fatal:", pushErr?.message); }
      // Navigate to on-page (embedded) Stripe checkout when publishable key is configured.
      // Falls back to hosted Stripe Checkout if the embedded key is missing.
      const hasEmbedded = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (hasEmbedded) {
        window.location.href = `/betaling/${booking.id}`;
      } else {
        const { data: session } = await api.post("/checkout/session", {
          booking_id: booking.id,
          origin_url: window.location.origin,
        });
        window.location.href = session.url;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = "Noget gik galt — prøv igen.";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI/Pydantic validation error array
        const fieldMap = {
          customer_email: "Email",
          customer_phone: "Telefon",
          customer_name: "Navn",
          pickup_address: "Afhentningsadresse",
          pickup_zip: "Afhentnings-postnr",
          pickup_city: "Afhentnings-by",
          delivery_address: "Leveringsadresse",
          delivery_zip: "Leverings-postnr",
          delivery_city: "Leverings-by",
          weight_kg: "Vægt",
          package_count: "Antal pakker",
          pickup_time: "Afhentningstid",
          company_name: "Firmanavn",
          cvr: "CVR",
        };
        const first = detail[0] || {};
        const field = (first.loc || []).filter((p) => p !== "body")[0];
        const dansk = fieldMap[field] || field || "Felt";
        const reason = first.msg || "";
        if (reason.toLowerCase().includes("email")) {
          msg = `${dansk}: ugyldig email-adresse`;
        } else if (reason.toLowerCase().includes("not a valid")) {
          msg = `${dansk}: ugyldig værdi`;
        } else if (reason.toLowerCase().includes("required") || reason.toLowerCase().includes("field required")) {
          msg = `${dansk} skal udfyldes`;
        } else {
          msg = `${dansk}: ${reason}`;
        }
      } else if (typeof detail === "object" && detail) {
        msg = detail.message || JSON.stringify(detail);
      }
      console.error("Booking submission failed:", e?.response?.data || e);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-brand-bg2 min-h-screen" data-testid="booking-page">
      {/* Service-paused banner — Fase 1 readiness gate */}
      {availability && !availability.accepting_orders && (
        <div className="bg-red-700 text-white py-4 px-5 sm:px-8 lg:px-12 border-b-4 border-red-900" data-testid="bookings-closed-banner">
          <div className="max-w-7xl mx-auto flex items-start gap-3">
            <span className="text-2xl flex-shrink-0 leading-none">🚧</span>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">Vi tager ikke imod nye bookinger lige nu</div>
              <div className="text-sm text-white/90 mt-1">{availability.message}</div>
              <a
                href="mailto:kontakt@emakurer.dk?subject=Bestilling%20%E2%80%94%20EMA%20Kurer"
                className="inline-block mt-3 bg-white text-red-700 hover:bg-red-50 font-bold text-sm px-4 py-2 transition-colors"
                data-testid="bookings-closed-mail-cta"
              >
                Skriv til os — kontakt@emakurer.dk
              </a>
            </div>
          </div>
        </div>
      )}

      {params.get("ref") && /^REF[A-F0-9]{6}$/i.test(params.get("ref") || "") && (
        <div
          className="bg-green-600 text-white text-center text-sm py-2.5 px-4 font-semibold"
          data-testid="referral-incoming-banner"
        >
          🎁 Du har 50 kr rabat fra anbefalingskoden <span className="font-mono font-bold tracking-wider">{params.get("ref")?.toUpperCase()}</span> — den anvendes automatisk ved kassen.
        </div>
      )}
      <section className="bg-brand-dark text-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red">Book din kurér</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 leading-tight">
            Direkte kurérservice · A → B
          </h1>
          <p className="text-white/70 mt-3 max-w-2xl">Vi henter pakken hos dig og kører den direkte til modtageren — ingen lager, ingen omlastning. Same-Day · Express · Aftenlevering · Erhvervskurér. Fra 199 kr inkl. moms (8 km inkluderet · +6 kr/km derefter).</p>
        </div>
      </section>

      {/* Progress */}
      <section className="bg-white border-b border-brand sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between gap-2" data-testid="booking-progress">
          {STEPS.map((s, i) => {
            const done = step > s.k;
            const active = step === s.k;
            return (
              <React.Fragment key={s.k}>
                <div className={`flex items-center gap-2 ${active ? "text-brand-dark" : done ? "text-brand-red" : "text-brand-text2/60"}`}>
                  <div className={`w-7 h-7 flex items-center justify-center text-xs font-bold ${active ? "bg-brand-dark text-white" : done ? "bg-brand-red text-white" : "bg-slate-200 text-brand-text2"}`}>
                    {done ? <CheckCircle2 size={14} /> : s.k}
                  </div>
                  <span className="text-xs sm:text-sm font-semibold hidden sm:inline">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${done ? "bg-brand-red" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-8 lg:py-12">
        {cancelled && (
          <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-900 px-5 py-4 text-sm" data-testid="cancelled-banner">
            Betaling blev annulleret. Du kan trygt prøve igen.
          </div>
        )}
        {reorderState === "loaded" && (
          <div className="mb-6 bg-brand-dark text-white px-5 py-4 text-sm flex items-center gap-3" data-testid="reorder-banner">
            <CheckCircle2 size={16} className="text-brand-red flex-shrink-0" />
            <div>
              <strong>Genbestil aktiv</strong> — vi har udfyldt formularen ud fra booking <span className="font-mono">{reorderBn}</span>. Tjek dato/tid, ret evt. og betal.
            </div>
          </div>
        )}
        {reorderState === "error" && (
          <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-900 px-5 py-4 text-sm" data-testid="reorder-error">
            Vi kunne ikke finde booking <span className="font-mono">{reorderBn}</span> til genbestilling. Udfyld manuelt — så er du i gang på 2 minutter.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="booking-form">
          <div className="lg:col-span-8">
            {step === 1 && (
              <div className="bg-white border border-brand p-6 sm:p-8" data-testid="step-1">
                <div className="kicker">Trin 1 af 4</div>
                <h2 className="font-display font-semibold text-2xl text-brand-dark mt-2">Afsender (dig)</h2>
                <p className="text-sm text-brand-text2 mt-1">Dine kontaktoplysninger som afsender. Vi sender fakturaen til din email.</p>

                <div className="grid grid-cols-2 gap-3 mt-6">
                  {[{ v: "private", t: "Privat" }, { v: "business", t: "Erhverv" }].map((o) => (
                    <button type="button" key={o.v} onClick={() => setForm((f) => ({ ...f, customer_type: o.v }))} data-testid={`customer-type-${o.v}`}
                      className={`py-3.5 px-4 border-2 text-left font-semibold transition-colors ${form.customer_type === o.v ? "border-brand-dark bg-brand-dark text-white" : "border-brand text-brand-dark hover:border-brand-dark"}`}>
                      {o.t}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div><label className="label-flat">Fornavn *</label><input className="input-flat" value={form.customer_first_name} onChange={update("customer_first_name")} data-testid="input-first-name" placeholder="Fornavn" autoComplete="given-name" required /></div>
                  <div><label className="label-flat">Efternavn *</label><input className="input-flat" value={form.customer_last_name} onChange={update("customer_last_name")} data-testid="input-last-name" placeholder="Efternavn" autoComplete="family-name" required /></div>
                  <div className="sm:col-span-2"><label className="label-flat">Email *</label><input type="email" className="input-flat" value={form.customer_email} onChange={update("customer_email")} data-testid="input-email" placeholder="dit.navn@example.dk" autoComplete="email" required /></div>
                  <div className="sm:col-span-2"><label className="label-flat">Mobilnummer *</label><input type="tel" className="input-flat" value={form.customer_phone} onChange={update("customer_phone")} data-testid="input-phone" placeholder="+45 12 34 56 78" autoComplete="tel" required /></div>
                  {form.customer_type === "business" && (<>
                    <div><label className="label-flat">Firmanavn *</label><input className="input-flat" value={form.company_name} onChange={update("company_name")} data-testid="input-company" placeholder="Firmanavn ApS" autoComplete="organization" /></div>
                    <div><label className="label-flat">CVR (8 cifre)</label><input className="input-flat" value={form.cvr} onChange={update("cvr")} data-testid="input-cvr" placeholder="12345678" maxLength={10} /></div>
                  </>)}
                </div>
                <div className="mt-4 text-xs text-brand-text2 flex items-start gap-2">
                  <CheckCircle2 size={12} className="text-brand-red mt-0.5 flex-shrink-0" />
                  <span>Vi sender din faktura og leveringsopdateringer til den oplyste email. Mobilnummeret bruges af chaufføren ved afhentning og levering.</span>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="bg-white border border-brand p-6 sm:p-8" data-testid="step-2">
                <div className="kicker">Trin 2 af 4</div>
                <h2 className="font-display font-semibold text-2xl text-brand-dark mt-2">Hvor og hvorhen?</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-brand-dark font-display font-semibold"><MapPin size={16} className="text-brand-red" /> Afhentning</div>
                    <div>
                      <label className="label-flat">Adresse *</label>
                      <AddressAutocomplete
                        value={form.pickup_address}
                        onChange={(v) => setForm((f) => ({ ...f, pickup_address: v }))}
                        onSelect={({ address, zip, city }) => setForm((f) => ({ ...f, pickup_address: address, pickup_zip: zip, pickup_city: city }))}
                        placeholder="Begynd at skrive adresse..."
                        testId="input-pickup-address"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-1"><label className="label-flat">Postnr *</label><input className="input-flat" value={form.pickup_zip} onChange={update("pickup_zip")} data-testid="input-pickup-zip" required /></div>
                      <div className="col-span-2"><label className="label-flat">By *</label><input className="input-flat" value={form.pickup_city} onChange={update("pickup_city")} data-testid="input-pickup-city" required /></div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-brand-dark font-display font-semibold">
                        <MapPin size={16} className="text-brand-red" /> Levering
                      </div>
                      <div className="flex bg-brand-bg2 p-1 rounded-md border border-brand text-xs" data-testid="delivery-destination-toggle">
                        <button
                          type="button"
                          onClick={() => {
                            setUseDeliveryToPickup(false);
                            // Clear pickup-point ref AND any address fields we stamped from the shop
                            setForm((f) => ({
                              ...f,
                              pakkeshop_partner_id: "",
                              pakkeshop_name: "",
                              delivery_address: "",
                              delivery_zip: "",
                              delivery_city: "",
                            }));
                          }}
                          data-testid="delivery-to-address-btn"
                          className={`px-3 py-1.5 font-semibold flex items-center gap-1.5 transition-colors ${
                            !useDeliveryToPickup ? "bg-brand-dark text-white rounded" : "text-brand-text2 hover:text-brand-dark"
                          }`}
                        >
                          <MapPin size={11} /> Til adresse
                        </button>
                        <button
                          type="button"
                          onClick={() => setUseDeliveryToPickup(true)}
                          data-testid="delivery-to-pickup-btn"
                          className={`px-3 py-1.5 font-semibold flex items-center gap-1.5 transition-colors ${
                            useDeliveryToPickup ? "bg-brand-dark text-white rounded" : "text-brand-text2 hover:text-brand-dark"
                          }`}
                        >
                          <Store size={11} /> Pickup Point
                        </button>
                      </div>
                    </div>

                    {useDeliveryToPickup ? (
                      <PickupPointSelector
                        destinationZip={form.delivery_zip}
                        selectedId={form.pakkeshop_partner_id}
                        onSelect={(p) => setForm((f) => ({
                          ...f,
                          pakkeshop_partner_id: p.id,
                          pakkeshop_name: p.shop_name,
                          delivery_address: p.shop_address || "",
                          delivery_zip: p.shop_zip || "",
                          delivery_city: p.shop_city || "",
                        }))}
                      />
                    ) : (
                      <>
                        <div>
                          <label className="label-flat">Adresse *</label>
                          <AddressAutocomplete
                            value={form.delivery_address}
                            onChange={(v) => setForm((f) => ({ ...f, delivery_address: v }))}
                            onSelect={({ address, zip, city }) => setForm((f) => ({ ...f, delivery_address: address, delivery_zip: zip, delivery_city: city }))}
                            placeholder="Begynd at skrive adresse..."
                            testId="input-delivery-address"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-1"><label className="label-flat">Postnr *</label><input className="input-flat" value={form.delivery_zip} onChange={update("delivery_zip")} data-testid="input-delivery-zip" required /></div>
                          <div className="col-span-2"><label className="label-flat">By *</label><input className="input-flat" value={form.delivery_city} onChange={update("delivery_city")} data-testid="input-delivery-city" required /></div>
                        </div>
                      </>
                    )}

                    {useDeliveryToPickup && form.pakkeshop_partner_id && (
                      <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-3 text-xs rounded flex items-start gap-2" data-testid="pp-confirmed">
                        <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0" />
                        <div>
                          <strong>{form.pakkeshop_name}</strong> valgt som leveringsadresse.<br />
                          Modtageren får email + pickup-kode når pakken er klar.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5 bg-brand-bg2 border border-brand p-4 text-xs text-brand-text2 flex items-start gap-3">
                  <MapPin size={14} className="text-brand-red mt-0.5 flex-shrink-0" />
                  <span>Vi leverer kun inden for <strong className="text-brand-dark">Sjælland</strong>. Hvis postnummeret ligger udenfor, kontaktes du efter booking.</span>
                </div>

                {/* Recipient block */}
                <div className="mt-8 pt-8 border-t border-brand">
                  <div className="flex items-center gap-2 text-brand-dark font-display font-semibold text-lg">
                    <User size={18} className="text-brand-red" /> Modtager (på leveringsadressen)
                  </div>
                  <p className="text-xs text-brand-text2 mt-1">Chaufføren kontakter modtageren ved levering. Email er ikke nødvendig.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div><label className="label-flat">Modtagers fornavn *</label><input className="input-flat" value={form.recipient_first_name} onChange={update("recipient_first_name")} data-testid="input-recipient-first-name" placeholder="Fornavn" required /></div>
                    <div><label className="label-flat">Modtagers efternavn *</label><input className="input-flat" value={form.recipient_last_name} onChange={update("recipient_last_name")} data-testid="input-recipient-last-name" placeholder="Efternavn" required /></div>
                    <div className="sm:col-span-2"><label className="label-flat">Modtagers mobilnummer *</label><input type="tel" className="input-flat" value={form.recipient_phone} onChange={update("recipient_phone")} data-testid="input-recipient-phone" placeholder="+45 12 34 56 78" required /></div>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="bg-white border border-brand p-6 sm:p-8" data-testid="step-3">
                <div className="kicker">Trin 3 af 4</div>
                <h2 className="font-display font-semibold text-2xl text-brand-dark mt-2">Leveringstype & afhentningstid</h2>

                {/* Delivery type selector */}
                <div className="mt-5">
                  <label className="label-flat">Vælg leveringstype *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2" data-testid="delivery-types">
                    {DELIVERY_TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = form.delivery_type === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, delivery_type: t.id }))}
                          data-testid={`delivery-type-${t.id}`}
                          className={`text-left p-4 border-2 transition ${active ? t.color : "border-brand bg-white text-brand-dark hover:border-brand-dark"}`}
                        >
                          <div className="flex items-start gap-3">
                            <Icon size={20} className={active ? "text-white" : "text-brand-red"} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <span className={`font-display font-semibold ${active ? "text-white" : "text-brand-dark"}`}>{t.label}</span>
                                {t.price ? (
                                  <span className={`text-[10px] uppercase tracking-widest font-bold ${active ? "text-white" : "text-brand-dark"}`}>fra {t.price} kr</span>
                                ) : (
                                  <span className={`text-[10px] uppercase tracking-widest font-bold ${active ? "text-white" : "text-brand-red"}`}>Tilbud</span>
                                )}
                              </div>
                              <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-brand-text2"}`}>{t.desc}</div>
                              {t.basePrice && (
                                <div className={`text-[11px] mt-1.5 font-medium ${active ? "text-white/90" : "text-brand-text2"}`}>{t.fromLabel}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {isBusinessQuote && (
                    <div className="mt-3 bg-slate-50 border border-slate-300 p-4 text-xs text-slate-700">
                      <strong>Erhvervspriser aftales individuelt.</strong> Klik "Send forespørgsel" for at sende os dine oplysninger — vi vender tilbage med et skræddersyet tilbud.
                    </div>
                  )}
                </div>

                {/* Size & weight limits info */}
                <div className="mt-5 bg-amber-50 border border-amber-300 p-4 text-xs" data-testid="package-limits">
                  <div className="flex items-start gap-3">
                    <Package size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-bold text-amber-950 uppercase tracking-widest text-[10px]">Pakke-begrænsninger (personbil)</div>
                      <ul className="mt-2 space-y-1 text-amber-950">
                        <li>· <strong>Maks. vægt pr. pakke:</strong> 11 kg</li>
                        <li>· <strong>Maks. længde pr. pakke:</strong> 120 cm</li>
                        <li>· <strong>Maks. mål pr. pakke:</strong> 120 × 60 × 60 cm (L × B × H)</li>
                        <li>· <strong>Maks. antal pakker:</strong> 20 · Maks. samlet vægt: 100 kg</li>
                      </ul>
                      <div className="text-amber-800 mt-2">Pakker, der overstiger disse mål, kan afvises ved afhentning uden refusion.</div>
                    </div>
                  </div>
                </div>

                {/* Pricing rules info */}
                <div className="mt-3 bg-brand-bg2 border border-brand p-4 text-xs" data-testid="pricing-info">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-brand-red mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-bold text-brand-dark uppercase tracking-widest text-[10px]">Sådan beregnes prisen</div>
                      <ul className="mt-2 space-y-1 text-brand-text2">
                        <li>· <strong>1. pakke:</strong> 199 kr inkl. moms (afhentning + levering, inkluderet 8 km)</li>
                        <li>· <strong>Ekstra km:</strong> +6 kr/km (Same-Day/Aften) eller +8 kr/km (Express)</li>
                        <li>· <strong>Hver ekstra pakke til SAMME adresse:</strong> +50 kr</li>
                        <li>· <strong>Anden leveringsadresse?</strong> Opret en separat booking — start ved 199 kr igen</li>
                      </ul>
                      <div className="text-brand-text2 mt-2">Eksempel: 1 pakke · 15 km = 199 + 7×6 = <strong className="text-brand-dark">241 kr</strong>. 3 pakker samme adresse · 8 km = 199 + 50 + 50 = <strong className="text-brand-dark">299 kr</strong>.</div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-6">
                  <div><label className="label-flat">Antal pakker *</label><input type="number" min="1" max="20" className="input-flat" value={form.package_count} onChange={update("package_count")} data-testid="input-package-count" required /></div>
                  <div><label className="label-flat">Vægt (kg) · max 11 *</label><input type="number" min="0.1" max="11" step="0.1" className="input-flat" value={form.weight_kg} onChange={update("weight_kg")} data-testid="input-weight" required /></div>
                  <div>
                    <label className="label-flat">Afhentnings­dato *</label>
                    <input
                      type="date"
                      min={new Date().toISOString().slice(0, 10)}
                      className="input-flat"
                      value={form.pickup_date}
                      onChange={update("pickup_date")}
                      data-testid="input-pickup-date"
                      required
                    />
                  </div>
                  <div>
                    <label className="label-flat">Afhentningstid *</label>
                    <select className="input-flat" value={form.pickup_time} onChange={update("pickup_time")} data-testid="input-pickup-time">
                      {PICKUP_TIMES.map((t) => {
                        const past = slotIsPast(form.pickup_date, t);
                        return (
                          <option key={t} value={t} disabled={past}>
                            {t}{past ? " (passeret)" : ""}
                          </option>
                        );
                      })}
                    </select>
                    {slotIsPast(form.pickup_date, form.pickup_time) && (
                      <div className="text-xs text-brand-red mt-1.5 flex items-center gap-1.5">
                        ⚠️ Denne tid er allerede passeret — vælg en anden dato eller tid
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-5">
                  <label className="label-flat">Leveringsinstruktion (valgfri)</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    {[
                      "Aflevér ved døren",
                      "Læg i cykelskuret",
                      "Læg i garagen",
                      "Aflevér til nabo",
                      "Ring på dørtelefonen",
                    ].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setForm({ ...form, delivery_instruction: opt })}
                        data-testid={`instr-${opt}`}
                        className={`py-2 px-3 text-xs border ${form.delivery_instruction === opt ? "border-brand-dark bg-brand-dark text-white" : "border-brand text-brand-dark hover:border-brand-dark"}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  <input className="input-flat" value={form.delivery_instruction} onChange={update("delivery_instruction")} data-testid="input-delivery-instruction" placeholder="Eller skriv din egen instruktion..." />
                  <div className="text-xs text-brand-text2 mt-1">Fuldmagt til chauffør hvis du ikke er hjemme — fx "læg pakken bag plantekrukken til venstre".</div>
                </div>
                <div className="mt-5">
                  <label className="label-flat">Bemærkninger til afhentning (valgfri)</label>
                  <textarea rows={3} className="input-flat" value={form.notes} onChange={update("notes")} data-testid="input-notes" placeholder="F.eks. ringer på, koden er 1234, skrøbeligt indhold..." />
                </div>

                {/* Insurance add-on */}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, insurance: !form.insurance })}
                  className={`mt-5 w-full text-left border-2 p-4 transition-colors ${form.insurance ? "border-brand-red bg-red-50" : "border-brand hover:border-brand-dark"}`}
                  data-testid="toggle-insurance"
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 h-5 w-5 shrink-0 border-2 ${form.insurance ? "border-brand-red bg-brand-red" : "border-brand-dark"} flex items-center justify-center`}>
                      {form.insurance && <span className="text-white text-xs font-bold">✓</span>}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-display font-semibold text-brand-dark">Tilføj pakkeforsikring</span>
                        <span className="font-bold text-brand-red">+19 kr</span>
                      </div>
                      <p className="text-xs text-brand-text2 mt-1">
                        Op til <strong className="text-brand-dark">5.000 kr</strong> dækning ved beskadigelse eller bortkomst.
                        Pakker uden forsikring dækkes op til 500 kr iht. vores standardvilkår.
                      </p>
                    </div>
                  </div>
                </button>

                <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {PICKUP_TIMES.slice(0, 8).map((t) => (
                    <button key={t} type="button" onClick={() => setForm({ ...form, pickup_time: t })}
                      className={`py-2 border ${form.pickup_time === t ? "border-brand-dark bg-brand-dark text-white" : "border-brand text-brand-dark hover:border-brand-dark"}`}>
                      <Clock size={12} className="inline mr-1" /> {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="bg-white border border-brand p-6 sm:p-8" data-testid="step-4">
                <div className="kicker">Trin 4 af 4 · Gennemse</div>
                <h2 className="font-display font-semibold text-2xl text-brand-dark mt-2">Tjek og bekræft</h2>
                {!isBusinessQuote && feasBlocking && (
                  <div className="mt-5">
                    <FeasibilityPanel
                      verdict={feas.verdict}
                      reason={feas.reason}
                      data={feas}
                      form={form}
                      onPickNextDay={({ date, window: w }) => {
                        setForm((f) => ({ ...f, pickup_date: date, pickup_time: w.start }));
                        toast.info(`Tidspunkt opdateret til ${date} · ${w.label}`);
                      }}
                    />
                  </div>
                )}                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                  <div className="border-t-2 border-brand-red pt-3">
                    <div className="text-xs uppercase tracking-widest text-brand-text2">Afsender</div>
                    <div className="font-display font-semibold text-brand-dark mt-1">{form.customer_first_name} {form.customer_last_name}</div>
                    <div className="text-sm text-brand-text2">{form.customer_email}</div>
                    <div className="text-sm text-brand-text2">{form.customer_phone}</div>
                    {form.customer_type === "business" && <div className="text-xs text-brand-text2 mt-1">{form.company_name} {form.cvr && `· CVR ${form.cvr}`}</div>}
                  </div>
                  <div className="border-t-2 border-brand-red pt-3">
                    <div className="text-xs uppercase tracking-widest text-brand-text2">Modtager</div>
                    <div className="font-display font-semibold text-brand-dark mt-1">{form.recipient_first_name} {form.recipient_last_name}</div>
                    <div className="text-sm text-brand-text2">{form.recipient_phone}</div>
                  </div>
                  <div className="border-t-2 border-brand-red pt-3">
                    <div className="text-xs uppercase tracking-widest text-brand-text2">Pakke</div>
                    <div className="font-display font-semibold text-brand-dark mt-1">{form.package_count} × pakke ({form.weight_kg} kg)</div>
                    <div className="text-sm text-brand-text2">Afhentning kl. {form.pickup_time}</div>
                  </div>
                  <div className="border-t-2 border-brand-red pt-3">
                    <div className="text-xs uppercase tracking-widest text-brand-text2">Afhentning</div>
                    <div className="text-sm text-brand-dark mt-1">{form.pickup_address}</div>
                    <div className="text-sm text-brand-text2">{form.pickup_zip} {form.pickup_city}</div>
                  </div>
                  <div className="border-t-2 border-brand-red pt-3 sm:col-span-2">
                    <div className="text-xs uppercase tracking-widest text-brand-text2">Levering</div>
                    <div className="text-sm text-brand-dark mt-1">{form.delivery_address}</div>
                    <div className="text-sm text-brand-text2">{form.delivery_zip} {form.delivery_city}</div>
                  </div>
                </div>

                {form.delivery_instruction && (
                  <div className="border-t-2 border-brand-red pt-3 mt-5 bg-amber-50 -mx-5 -mb-5 px-5 pb-5 sm:rounded">
                    <div className="text-xs uppercase tracking-widest text-amber-900">Leveringsinstruktion</div>
                    <div className="text-sm text-brand-dark mt-1 font-semibold">{form.delivery_instruction}</div>
                  </div>
                )}
                {form.notes && (
                  <div className="border-t-2 border-brand-red pt-3 mt-5">
                    <div className="text-xs uppercase tracking-widest text-brand-text2">Bemærkning</div>
                    <div className="text-sm text-brand-dark mt-1">{form.notes}</div>
                  </div>
                )}
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6" data-testid="step-nav">
              {step > 1 ? (
                <button type="button" onClick={prev} className="btn-secondary" data-testid="prev-btn"><ArrowLeft size={16} /> Tilbage</button>
              ) : <span />}
              {step < 4 ? (
                <button type="button" onClick={next} className="btn-primary" data-testid="next-btn">Næste <ArrowRight size={16} /></button>
              ) : (
                <button type="button" onClick={submit} disabled={submitting || (feasBlocking && !isBusinessQuote) || (availability && !availability.accepting_orders)} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-booking">
                  {submitting ? <><Loader2 className="animate-spin" size={16} /> Behandler...</> : isBusinessQuote ? <>Send forespørgsel <ArrowRight size={18} /></> : feasBlocking ? <>Vælg alternativ ovenfor</> : <>Betal og book levering <ArrowRight size={18} /></>}
                </button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="bg-brand-dark text-white p-7 lg:sticky lg:top-32" data-testid="price-summary">
              <div className="kicker text-brand-red">Din ordre</div>
              <div className="font-display font-bold text-2xl mt-3">{DELIVERY_TYPES.find((t) => t.id === form.delivery_type)?.label || "Same-Day"}</div>
              <div className="text-white/60 text-sm mt-1">
                {isBusinessQuote ? "Tilbud sendes pr. email" : `Sjælland · kl. ${form.pickup_time}`}
              </div>

              {/* From-price headline — communicates "starts at" not "fixed" */}
              {!isBusinessQuote && (
                <div className="mt-4 inline-flex items-center gap-2 bg-white/5 border border-white/15 px-3 py-1.5 text-[11px] tracking-widest uppercase text-white/70" data-testid="from-price-badge">
                  Fra <span className="text-white font-semibold normal-case tracking-normal text-sm">{(DELIVERY_TYPES.find((t) => t.id === form.delivery_type)?.basePrice) || PRICE} kr</span> inkl. 8 km
                </div>
              )}

              {/* Live-status banner — always visible so customer knows exactly what's happening */}
              {!isBusinessQuote && (
                <div className="mt-4" data-testid="price-status-banner">
                  {quoteLoading ? (
                    <div className="flex items-center gap-2 bg-amber-500/15 border border-amber-400/40 text-amber-100 px-3 py-2.5 text-sm animate-pulse" data-testid="price-status-loading">
                      <Loader2 size={16} className="animate-spin text-amber-300" />
                      <span>Beregner din pris ud fra adresserne…</span>
                    </div>
                  ) : quoteError ? (
                    <div className="bg-red-500/15 border-2 border-red-400/60 text-red-100 px-3 py-2.5 text-sm" data-testid="price-status-error">
                      <div className="font-semibold flex items-center gap-2">
                        <span className="text-red-300">⚠</span> Prisen kunne ikke beregnes
                      </div>
                      <div className="text-red-200/90 text-xs mt-1 leading-snug">{quoteError}. Vælg adressen fra forslags-listen (DAWA), så postnr og by udfyldes automatisk.</div>
                    </div>
                  ) : quote ? (
                    <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-400/40 text-emerald-100 px-3 py-2 text-xs" data-testid="price-status-ok">
                      <CheckCircle2 size={14} className="text-emerald-300 flex-shrink-0" />
                      <span>Præcis pris beregnet — <strong className="text-white">{quote.distance_km} km rute</strong></span>
                    </div>
                  ) : (
                    <div className="bg-white/5 border border-white/15 text-white/80 px-3 py-2.5 text-sm" data-testid="price-status-info">
                      <div className="font-semibold flex items-center gap-2">
                        <MapPin size={14} className="text-brand-red" /> Udfyld begge adresser
                      </div>
                      <div className="text-white/60 text-xs mt-1 leading-snug">Gå til trin 2 &laquo;Adresser&raquo; og udfyld både afhentnings- og leveringsadresse for at se din endelige pris (inkl. rute-afstand).</div>
                    </div>
                  )}
                </div>
              )}

              {/* Live quote breakdown */}
              {isBusinessQuote ? (
                <div className="border-t border-white/10 mt-6 pt-6 text-sm text-white/80 leading-relaxed">
                  Erhvervspriser aftales individuelt. Klik <strong>"Send forespørgsel"</strong> for at sende os dine oplysninger — vi vender tilbage med tilbud.
                </div>
              ) : quote ? (
                <div className="border-t border-white/10 mt-6 pt-6 space-y-2.5 text-sm" data-testid="quote-breakdown">
                  {/* Startpris row */}
                  <div className="flex justify-between">
                    <div>
                      <div className="text-white font-medium">{quote.label} startpris</div>
                      <div className="text-white/50 text-xs mt-0.5">Inkl. de første {quote.km_included} km</div>
                    </div>
                    <span className="font-semibold">{quote.base_dkk} kr</span>
                  </div>

                  {/* Distance — actual route */}
                  <div className="flex justify-between text-xs pt-1">
                    <span className="text-white/50">Beregnet rute: {quote.distance_km} km · ~{quote.estimated_duration_min || "?"} min</span>
                  </div>

                  {/* Extra km — show the multiplication */}
                  {quote.distance_fee_dkk > 0 && (
                    <div className="flex justify-between pt-2 border-t border-white/5">
                      <div>
                        <div className="text-white/85">Ekstra distance</div>
                        <div className="text-white/50 text-xs mt-0.5">{quote.extra_km} km × {quote.price_per_extra_km || 6} kr = {quote.distance_fee_dkk} kr</div>
                      </div>
                      <span className="font-semibold">+{quote.distance_fee_dkk} kr</span>
                    </div>
                  )}

                  {/* Extra packages — multiplication */}
                  {quote.extra_package_fee_dkk > 0 && (
                    <div className="flex justify-between pt-2 border-t border-white/5">
                      <div>
                        <div className="text-white/85">Ekstra pakker</div>
                        <div className="text-white/50 text-xs mt-0.5">{Number(form.package_count) - 1} stk × 50 kr = {quote.extra_package_fee_dkk} kr</div>
                      </div>
                      <span className="font-semibold">+{quote.extra_package_fee_dkk} kr</span>
                    </div>
                  )}

                  {/* Weekend surcharge with reason */}
                  {quote.weekend_fee_dkk > 0 && (
                    <div className="flex justify-between text-amber-300 pt-2 border-t border-white/5">
                      <div>
                        <div className="font-medium">Weekendtillæg</div>
                        <div className="text-amber-200/60 text-xs mt-0.5">+20% fordi leveringen er lørdag/søndag</div>
                      </div>
                      <span className="font-semibold">+{quote.weekend_fee_dkk} kr</span>
                    </div>
                  )}

                  {/* Late-evening surcharge with reason */}
                  {quote.late_evening_fee_dkk > 0 && (
                    <div className="flex justify-between text-amber-300 pt-2 border-t border-white/5">
                      <div>
                        <div className="font-medium">Sen-aften-tillæg</div>
                        <div className="text-amber-200/60 text-xs mt-0.5">+15% fordi afhentning er efter kl. 20</div>
                      </div>
                      <span className="font-semibold">+{quote.late_evening_fee_dkk} kr</span>
                    </div>
                  )}

                  {/* Insurance line */}
                  {quote.insurance_fee_dkk > 0 && (
                    <div className="flex justify-between text-brand-red pt-2 border-t border-white/5" data-testid="quote-insurance-line">
                      <div>
                        <div className="font-medium">Pakkeforsikring</div>
                        <div className="text-white/50 text-xs mt-0.5">Op til 5.000 kr dækning</div>
                      </div>
                      <span className="font-semibold">+{quote.insurance_fee_dkk} kr</span>
                    </div>
                  )}

                  {discount.status === "valid" && (
                    <div className="flex justify-between text-[#34d399] pt-2 border-t border-white/5" data-testid="discount-line">
                      <span>Rabat ({discount.info?.percent}%)</span>
                      <span className="font-semibold">-{discount.info?.discount_dkk} kr</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-t border-white/10 mt-6 pt-6 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-white/70">Startpris <span className="text-white/40 text-[10px]">(inkl. moms)</span></span><span className="font-semibold">{PRICE} kr</span></div>
                  {Number(form.package_count || 1) > 1 && (
                    <div className="flex justify-between" data-testid="extra-packages-line">
                      <span className="text-white/70">+{Number(form.package_count) - 1} ekstra pakke{Number(form.package_count) - 1 > 1 ? "r" : ""} × {EXTRA_PRICE} kr</span>
                      <span className="font-semibold">{(Number(form.package_count) - 1) * EXTRA_PRICE} kr</span>
                    </div>
                  )}
                  <div className="text-xs text-white/50 pt-1" data-testid="quote-hint">
                    + evt. km-tillæg efter rute-afstand (6 kr/km over 8 km inkluderet)
                  </div>
                </div>
              )}

              {/* Discount input */}
              <div className="border-t border-white/10 mt-5 pt-5">
                {discount.status === "valid" ? (
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono bg-white/10 px-2 py-1">{discount.code}</span>
                    <button type="button" onClick={removeDiscount} className="text-white/60 hover:text-white underline" data-testid="discount-remove">Fjern</button>
                  </div>
                ) : (
                  <div className="flex gap-2" data-testid="discount-input-row">
                    <input
                      className="input-flat flex-1 text-brand-dark py-2 text-sm uppercase tracking-wider"
                      placeholder="Rabatkode"
                      value={discount.code}
                      onChange={(e) => setDiscount({ ...discount, code: e.target.value.toUpperCase(), status: "idle" })}
                      data-testid="discount-input"
                    />
                    <button type="button" onClick={applyDiscount} disabled={discount.status === "checking"} className="btn-primary py-2 px-3 text-xs" data-testid="discount-apply">
                      {discount.status === "checking" ? <Loader2 className="animate-spin" size={14} /> : "Anvend"}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5 pt-5 border-t border-white/10 flex items-end justify-between">
                <div>
                  <span className="text-xs uppercase tracking-widest text-white/60">Total</span>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{isBusinessQuote ? "Tilbud sendes pr. mail" : "Inkl. 25% moms"}</div>
                </div>
                <span className={`font-display font-bold text-4xl text-white transition-all duration-300 ${quoteLoading ? "opacity-60 animate-pulse" : ""}`} data-testid="total-amount">
                  {isBusinessQuote ? "Tilbud" : `${total}`} {!isBusinessQuote && <span className="text-base text-white/70 font-medium">kr</span>}
                </span>
              </div>
              {!isBusinessQuote && !quote && !quoteLoading && !quoteError && (
                <div className="text-[11px] text-white/50 mt-2 italic" data-testid="total-hint-estimate">
                  ↑ Foreløbig — endelig pris beregnes når begge adresser er udfyldt
                </div>
              )}

              {/* "Dette er inkluderet" — value-explainer */}
              {!isBusinessQuote && (
                <div className="mt-6 bg-white/5 border border-white/10 p-4" data-testid="included-box">
                  <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">Dette er inkluderet</div>
                  <ul className="mt-3 space-y-1.5 text-xs text-white/80">
                    {[
                      "Direkte afhentning hos dig",
                      "Direkte levering til modtager",
                      "Ingen lager eller omlastning",
                      "QR-bekræftet afhentning",
                      "Live tracking efter betaling",
                      "Sikker online betaling",
                    ].map((it) => (
                      <li key={it} className="flex gap-2"><CheckCircle2 size={12} className="text-brand-red mt-0.5 flex-shrink-0" /> {it}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* "Direkte kurér — ikke fragt" — differentiator from PostNord/DAO */}
              {!isBusinessQuote && (
                <div className="mt-3 bg-brand-red/10 border border-brand-red/40 p-4" data-testid="vs-freight-box">
                  <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">Direkte kurér — ikke fragt</div>
                  <p className="text-xs text-white/85 mt-2 leading-relaxed">
                    EMA Kurer henter pakken på din adresse og kører den <strong>direkte</strong> til modtageren. Leveringen går ikke via lager, terminal eller omlastning.
                  </p>
                </div>
              )}

              <ul className="text-xs text-white/60 mt-6 space-y-2">
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 flex-shrink-0" /> Alle priser er inkl. 25% moms</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 flex-shrink-0" /> Faktura sendes automatisk til din email</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 flex-shrink-0" /> Sikker Stripe-betaling</li>
                <li className="flex gap-2"><CheckCircle2 size={14} className="text-brand-red mt-0.5 flex-shrink-0" /> Spor i realtid efter betaling</li>
              </ul>
            </div>

            <div className="bg-white border border-brand p-5 mt-5 text-sm text-brand-text2">
              Brug for hjælp? Skriv til <a className="text-brand-red font-semibold underline" href="mailto:kontakt@emakurer.dk">kontakt@emakurer.dk</a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
